import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
} from "@react-pdf/renderer";

const A4 = { width: "210mm", height: "297mm" };

const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const darken = (hex, factor = 0.7) => {
  const { r, g, b } = hexToRgb(hex);
  const f = (n) => Math.max(0, Math.min(255, Math.round(n * factor)))
    .toString(16)
    .padStart(2, "0");
  return `#${f(r)}${f(g)}${f(b)}`;
};

const pickBannerText = (hex, hint) => {
  if ((hint || "").toLowerCase() === "light") return "#1a1a1a";
  if ((hint || "").toLowerCase() === "dark") return "#ffffff";
  const { r, g, b } = hexToRgb(hex);
  const lin = (c) => {
    const cc = c / 255;
    return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return lum > 0.4 ? "#1a1a1a" : "#ffffff";
};

const tint = (hex, alpha = 0.12) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};

const IconBolt = ({ color }) => (
  <Svg viewBox="0 0 320 512" style={{ width: 9, height: 9 }}>
    <Path
      fill={color}
      d="M296 160H180.6l42.6-129.8C227.2 16.5 215.7 0 200.9 0H56C44.2 0 34.2 8.6 32.4 20.3L.6 220.3C-1.6 234.6 9.4 248 24 248h118.7L96.6 482.5C93.8 496.6 105.4 512 119.9 512c8 0 15.7-4 20.2-11.1l175.2-272c11.6-18-1.1-41.9-19.3-68.9z"
    />
  </Svg>
);

const IconCheck = ({ color }) => (
  <Svg viewBox="0 0 512 512" style={{ width: 9, height: 9 }}>
    <Path
      fill={color}
      d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm141.4 184.6l-150 168c-4.5 5-10.9 7.9-17.6 7.9-6.4 0-12.5-2.5-17-7.1l-90-90c-9.4-9.4-9.4-24.6 0-33.9 9.4-9.4 24.6-9.4 33.9 0l71.7 71.7 133-148.9c8.8-9.8 23.9-10.7 33.7-1.9 9.9 8.8 10.7 23.9 1.9 33.7z"
    />
  </Svg>
);

const IconLock = ({ color }) => (
  <Svg viewBox="0 0 448 512" style={{ width: 9, height: 9 }}>
    <Path
      fill={color}
      d="M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z"
    />
  </Svg>
);

const IconPin = ({ color }) => (
  <Svg viewBox="0 0 384 512" style={{ width: 9, height: 12 }}>
    <Path
      fill={color}
      d="M172.3 501.7C27 291 0 269.4 0 192 0 86 86 0 192 0s192 86 192 192c0 77.4-27 99-172.3 309.7-9.5 13.7-29.9 13.7-39.4 0z"
    />
  </Svg>
);

const buildStyles = (colour, colourDark, bannerText) =>
  StyleSheet.create({
    page: {
      width: A4.width,
      height: A4.height,
      padding: "26mm 22mm",
      backgroundColor: "#ffffff",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    card: {
      width: "100%",
      maxWidth: "140mm",
      flexDirection: "column",
      borderRadius: 6,
      overflow: "hidden",
      borderWidth: 0.6,
      borderColor: "#ececec",
    },
    agencyBar: {
      backgroundColor: colour,
      paddingTop: "5mm",
      paddingBottom: "5mm",
      paddingLeft: "8mm",
      paddingRight: "8mm",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    logo: { maxHeight: "10mm", maxWidth: "50mm", objectFit: "contain" },
    agencyName: {
      color: bannerText,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.3,
    },
    hero: {
      width: "100%",
      paddingTop: "10mm",
      paddingBottom: "10mm",
      paddingLeft: "8mm",
      paddingRight: "8mm",
      backgroundColor: colourDark,
      alignItems: "center",
      justifyContent: "center",
    },
    eyebrow: {
      color: bannerText,
      fontSize: 8.5,
      letterSpacing: 2,
      opacity: 0.85,
      marginBottom: 6,
      textTransform: "uppercase",
    },
    headline: {
      color: bannerText,
      fontSize: 18,
      fontWeight: 700,
      textAlign: "center",
      lineHeight: 1.25,
    },
    subtitle: {
      color: bannerText,
      opacity: 0.9,
      fontSize: 10,
      textAlign: "center",
      marginTop: 6,
      lineHeight: 1.4,
      maxWidth: "110mm",
    },
    cta: {
      paddingTop: "8mm",
      paddingBottom: "6mm",
      paddingLeft: "8mm",
      paddingRight: "8mm",
      alignItems: "center",
    },
    scanHeadline: {
      fontSize: 13,
      fontWeight: 700,
      color: "#111827",
      textAlign: "center",
      marginBottom: "5mm",
    },
    scanHeadlineEm: { color: colour, fontWeight: 700 },
    trustRow: {
      flexDirection: "row",
      justifyContent: "center",
      marginBottom: "5mm",
      gap: 4,
    },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      paddingTop: 3,
      paddingBottom: 3,
      paddingLeft: 7,
      paddingRight: 7,
      borderRadius: 12,
      backgroundColor: tint(colour, 0.12),
      marginHorizontal: 2,
    },
    chipText: {
      fontSize: 7.5,
      fontWeight: 700,
      color: colourDark,
      marginLeft: 3,
      letterSpacing: 0.3,
    },
    qrWrap: {
      width: "52mm",
      height: "52mm",
      padding: 5,
      backgroundColor: "#ffffff",
      borderWidth: 0.6,
      borderColor: "#e5e7eb",
      borderRadius: 4,
      alignItems: "center",
      justifyContent: "center",
    },
    qr: { width: "100%", height: "100%" },
    scanPrompt: {
      marginTop: "3mm",
      fontSize: 8.5,
      color: colour,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    inPersonNote: {
      marginTop: "3mm",
      fontSize: 8.5,
      color: "#4b5563",
      textAlign: "center",
      maxWidth: "100mm",
      lineHeight: 1.4,
    },
    agent: {
      flexDirection: "row",
      alignItems: "center",
      paddingTop: "5mm",
      paddingBottom: "6mm",
      paddingLeft: "8mm",
      paddingRight: "8mm",
      borderTop: "0.5pt solid #e5e7eb",
      backgroundColor: "#fafafa",
    },
    avatar: {
      width: "17mm",
      height: "17mm",
      borderRadius: 24,
      marginRight: "4mm",
      objectFit: "cover",
      backgroundColor: "#e5e7eb",
    },
    name: { fontSize: 11, fontWeight: 700, color: "#111827" },
    title: { fontSize: 8, color: "#6b7280", marginTop: 1 },
    contactRow: { flexDirection: "row", marginTop: 2 },
    contactLabel: {
      color: colour,
      fontWeight: 700,
      fontSize: 8.5,
      width: "11mm",
    },
    contactValue: { color: "#374151", fontSize: 8.5 },
  });

export default function GenericQrDocument({ agent, qrDataUrl, headline, subtitle, eyebrow }) {
  const colour = agent?.agent_colour || "#2563eb";
  const colourDark = darken(colour, 0.7);
  const bannerText = pickBannerText(colour, agent?.agent_colour_lightness);
  const styles = buildStyles(colour, colourDark, bannerText);

  const finalHeadline = headline || "What's your home really worth?";
  const finalSubtitle = subtitle || "Get a free, instant property report — no obligation, no sign-up.";
  const finalEyebrow = eyebrow || "Free property estimate";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.card}>
          <View style={styles.agencyBar}>
            {agent?.logo_url ? (
              <Image src={agent.logo_url} style={styles.logo} />
            ) : (
              <View />
            )}
            <Text style={styles.agencyName}>{agent?.agency_name || ""}</Text>
          </View>

          <View style={styles.hero}>
            <Text style={styles.eyebrow}>{finalEyebrow}</Text>
            <Text style={styles.headline}>{finalHeadline}</Text>
            <Text style={styles.subtitle}>{finalSubtitle}</Text>
          </View>

          <View style={styles.cta}>
            <Text style={styles.scanHeadline}>
              Scan the code for an{" "}
              <Text style={styles.scanHeadlineEm}>instant estimate</Text>
            </Text>

            <View style={styles.trustRow}>
              <View style={styles.chip}>
                <IconBolt color={colourDark} />
                <Text style={styles.chipText}>Instant estimate</Text>
              </View>
              <View style={styles.chip}>
                <IconCheck color={colourDark} />
                <Text style={styles.chipText}>100% free</Text>
              </View>
              <View style={styles.chip}>
                <IconLock color={colourDark} />
                <Text style={styles.chipText}>No sign-up</Text>
              </View>
              <View style={styles.chip}>
                <IconPin color={colourDark} />
                <Text style={styles.chipText}>Local insights</Text>
              </View>
            </View>

            <View style={styles.qrWrap}>
              {qrDataUrl && <Image src={qrDataUrl} style={styles.qr} />}
            </View>
            <Text style={styles.scanPrompt}>Scan with your phone camera</Text>
            {agent?.agent_phone ? (
              <Text style={styles.inPersonNote}>
                Prefer an in-person price update?
                {"\n"}
                Call {(agent.agent_name || "your agent").split(" ")[0]} on {agent.agent_phone}
              </Text>
            ) : null}
          </View>

          <View style={styles.agent}>
            {agent?.profile_image_url ? (
              <Image src={agent.profile_image_url} style={styles.avatar} />
            ) : (
              <View style={styles.avatar} />
            )}
            <View style={{ flexShrink: 1 }}>
              <Text style={styles.name}>{agent?.agent_name || ""}</Text>
              <Text style={styles.title}>{agent?.agent_title || ""}</Text>
              {agent?.agent_phone ? (
                <View style={styles.contactRow}>
                  <Text style={styles.contactLabel}>Call</Text>
                  <Text style={styles.contactValue}>{agent.agent_phone}</Text>
                </View>
              ) : null}
              {agent?.agent_email ? (
                <View style={styles.contactRow}>
                  <Text style={styles.contactLabel}>Email</Text>
                  <Text style={styles.contactValue}>{agent.agent_email}</Text>
                </View>
              ) : null}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
